import { Table, Button, message } from 'antd';
import UserHeader from '../../components/user/Header';
import React, { useCallback, useEffect, useState } from 'react';
import { ExamQuestion } from '../../types/ExamQuestion';
import { examQuestionService } from '../../services/examQuestion';
import { useNavigate } from 'react-router-dom';
import { BookFilled, CustomerServiceFilled } from '@ant-design/icons';
import Colors from '../../common/color';
import Fontsizes from '../../common/fontSize';
import FontWeights from '../../common/fontWeight';
import { User } from '@/types/User';
import { linkDocumentService } from '../../services/linkDocumentService';

interface ApiResponse {
    req: {
        page: string;
        limit: string;
        search: string;
    };
    data: ExamQuestion[];
    total: number;
    currentPage: number;
    totalPages: number;
}

const Home: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [dataSource, setDataSource] = useState<any[]>([]);
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [linkDocument, setLinkDocument] = useState<any>(null);
    const fetchExamQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const response: ApiResponse = await examQuestionService.getPrivateExamQuestions({
                page: currentPage,
                pageSize: pageSize,
            });
            // filter exam question type = private
            let examQuestion = response.data.filter((item: any) => item.type === 'private');
            setDataSource(examQuestion);
            setTotal(response.total);
        } catch (error) {
            message.error('Failed to fetch exam questions');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    const fetchLinkDocument = useCallback(async () => {
        const response = await linkDocumentService.getLinkDocument();
        setLinkDocument(response);
    }, []);

    useEffect(() => {
        fetchExamQuestions();
        fetchLinkDocument();
    }, [fetchExamQuestions, fetchLinkDocument]);

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            setUser(JSON.parse(savedUser));
        }
    }, []);

    const columns = [
        {
            title: <span className='font-semibold text-lg'>Practice Modules</span>,
            dataIndex: 'name',
            key: 'name',
            width: '40%',
            render: (name: string) => <p className='text-gray-700 font-medium'>{name}</p>,
        },
        {
            title: <div className='flex items-center justify-center'>
                <BookFilled className='mr-2 text-lg' style={{ color: Colors.primary }} />
                <span className='font-semibold text-lg'>Reading</span>
            </div>,
            dataIndex: 'ExamQuestionSkills',
            key: 'ExamQuestionSkills',
            width: '30%',
            render: (ExamQuestionSkills) => {
                let isHasReading = false;
                let examQuestionSkillParam;
                for (const examQuestionSkill of ExamQuestionSkills) {
                    if (examQuestionSkill.Skill.name === 'Reading') {
                        examQuestionSkillParam = examQuestionSkill;
                        isHasReading = true;
                        break;
                    }
                }
                return isHasReading ? (
                    <Button
                        type="primary"
                        className="w-full border-none hover:opacity-90 transition-opacity duration-300 font-medium text-base h-10"
                        style={{ backgroundColor: Colors.primary }}
                        onClick={() => navigate('/exam-info', {
                            state: { examQuestionSkill: examQuestionSkillParam }
                        })}
                    >
                        Take Test
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        className="w-full border-none font-medium text-base h-10"
                        disabled
                    >
                        Take Test
                    </Button>
                );
            },
        },
        {
            title: <div className='flex items-center justify-center'>
                <CustomerServiceFilled className='mr-2 text-lg' style={{ color: Colors.primary }} />
                <span className='font-semibold text-lg'>Listening</span>
            </div>,
            dataIndex: 'ExamQuestionSkills',
            key: 'ExamQuestionSkills',
            width: '30%',
            render: (ExamQuestionSkills) => {
                let isHasListening = false;
                let examQuestionSkillParam;
                for (const examQuestionSkill of ExamQuestionSkills) {
                    if (examQuestionSkill.Skill.name === 'Listening') {
                        examQuestionSkillParam = examQuestionSkill;
                        isHasListening = true;
                        break;
                    }
                }
                return isHasListening ? (
                    <Button
                        type="primary"
                        className="w-full border-none hover:opacity-90 transition-opacity duration-300 font-medium text-base h-10"
                        style={{ backgroundColor: Colors.primary }}
                        onClick={() => navigate(
                            '/exam-info',
                            {
                                state: { examQuestionSkill: examQuestionSkillParam }
                            }
                        )}
                    >
                        Take Test
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        className="w-full border-none font-medium text-base h-10"
                        disabled
                    >
                        Take Test
                    </Button>
                );
            }
        },
    ];

    const handleTableChange = (newPagination: any) => {
        setCurrentPage(newPagination.current);
        setPageSize(newPagination.pageSize);
    };

    return (
        <div className="min-h-screen from-gray-50 to-white" style={{ fontWeight: FontWeights.normal, fontSize: Fontsizes.lg }}>
            <UserHeader />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl md:text-4xl font-bold text-center mt-[120px] text-gray-800 mb-8 md:mb-12">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-400" style={{ color: Colors.primary }}>
                        Hien Le Aptis
                    </span>
                </h1>

                <div className="max-w-4xl mx-auto space-y-4">
                    {user?.isViewVideo === '1' && (
                        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
                                <div className="flex items-center w-full sm:w-auto">
                                    <div className="p-2 rounded-full bg-primary-50" style={{ backgroundColor: `${Colors.primary}20` }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke={Colors.primary}>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span className="text-xl md:text-2xl font-semibold text-gray-800 ml-4">Video Tutorial</span>
                                </div>
                                <button
                                    onClick={() => navigate('/video')}
                                    className="w-full sm:w-auto px-4 md:px-6 py-2 rounded-lg font-medium transition-all duration-300 hover:translate-x-1 text-center"
                                    style={{ color: Colors.primary }}
                                >
                                    Watch Now →
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
                            <div className="flex items-center w-full sm:w-auto">
                                <div className="p-2 rounded-full bg-primary-50" style={{ backgroundColor: `${Colors.primary}20` }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke={Colors.primary}>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                    </svg>
                                </div>
                                <span className="text-xl md:text-2xl font-semibold text-gray-800 ml-4">
                                    Speaking and Writing Practice
                                </span>
                            </div>
                            <a
                                href={linkDocument?.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto px-4 md:px-6 py-2 rounded-lg font-medium transition-all duration-300 hover:translate-x-1 text-center"
                                style={{ color: Colors.primary }}
                            >
                                Start Practice →
                            </a>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 md:p-8 shadow-md border border-gray-100">
                        <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6 md:mb-8 flex items-center">
                            <div className="p-2 rounded-full bg-primary-50 mr-3 md:mr-4" style={{ backgroundColor: `${Colors.primary}20` }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke={Colors.primary}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            Listening and Reading Practice
                        </h2>
                        <div className="overflow-x-auto">
                            <Table
                                className="custom-table"
                                dataSource={dataSource}
                                columns={columns as any}
                                rowKey="id"
                                size="middle"
                                bordered
                                loading={loading}
                                onChange={handleTableChange}
                                pagination={{
                                    size: 'small',
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: total,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    pageSizeOptions: [5, 10, 20],
                                }}
                                scroll={{ x: 'max-content' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
